import React, { useState, useEffect, useCallback, useRef } from "react";
import Select from "react-select";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import FormInput from "../../components/CustomComponents/Input/formInput";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";

const ALL_POSTAL_CODES_VALUE = "AllPostalCodes";

const MATCH_TYPE_OPTIONS = [
  {
    value: ALL_POSTAL_CODES_VALUE,
    label: "All postal codes",
    helper:
      "Applies to every delivery when no more specific postcode rule matches. No pattern is needed.",
  },
  {
    value: "Exact",
    label: "Exact",
    helper:
      "Only customers whose postcode is exactly this (full postcode) pay this fee. You can add several postcodes separated by commas, e.g. SW1A 1AA, EH1 1YZ.",
  },
  {
    value: "Prefix",
    label: "Prefix",
    helper:
      "Customers whose postcode starts with these letters or numbers pay this fee, e.g. SW1 matches SW1A 1AA and SW1 9ZZ.",
  },
];

const emptyRuleForm = {
  ruleID: 0,
  matchType: "Prefix",
  pattern: "",
  fee: "",
  priority: 0,
  isDefault: false,
  isActive: true,
};

const normalizeExactPatternList = (raw) => {
  if (!raw || !String(raw).trim()) return "";
  return String(raw)
    .split(",")
    .map((p) => p.trim().replace(/\s+/g, " ").toUpperCase())
    .filter(Boolean)
    .join(", ");
};

const formatRule = (rule) => ({
  ruleID: rule.ruleID ?? rule.RuleID ?? 0,
  compID: rule.compID ?? rule.CompID ?? 0,
  matchType: rule.matchType ?? rule.MatchType ?? "Exact",
  pattern: rule.pattern ?? rule.Pattern ?? "",
  fee: Number(rule.fee ?? rule.Fee ?? 0),
  priority: Number(rule.priority ?? rule.Priority ?? 0),
  isDefault: !!(rule.isDefault ?? rule.IsDefault),
  isActive: !!(rule.isActive ?? rule.IsActive),
});

/** Non-default first (priority DESC), then all-postal-codes fallbacks. */
const sortRulesForDisplay = (list) =>
  [...list].sort((a, b) => {
    if (a.isDefault !== b.isDefault) return a.isDefault ? 1 : -1;
    if (b.priority !== a.priority) return b.priority - a.priority;
    return a.ruleID - b.ruleID;
  });

/** Top row gets the highest priority (DESC matching on the server). */
const assignPrioritiesFromOrder = (orderedRules) => {
  const sortable = orderedRules.filter((r) => !r.isDefault);
  const defaults = orderedRules.filter((r) => r.isDefault);
  const withPriority = sortable.map((rule, index) => ({
    ...rule,
    priority: sortable.length - index,
  }));
  return [
    ...withPriority,
    ...defaults.map((rule) => ({ ...rule, priority: 0 })),
  ];
};

/** Label with a leading info icon; full help text on hover (and for screen readers). */
const FieldLabelWithInfo = ({ labelText, infoText }) => (
  <label className="form-label fw-semibold d-flex align-items-center gap-1 flex-wrap">
    <span
      className="text-info"
      title={infoText}
      role="img"
      aria-label={infoText}
      tabIndex={0}
      style={{ cursor: "help", lineHeight: 1, fontSize: "0.95rem" }}
    >
      <i className="fas fa-info-circle" aria-hidden="true" />
    </span>
    <span>{labelText}</span>
  </label>
);

const DeliveryChargeRules = ({ CompID, currencySymbol = "" }) => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reordering, setReordering] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [form, setForm] = useState(emptyRuleForm);
  const [submitting, setSubmitting] = useState(false);

  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const fetchRules = useCallback(async () => {
    if (!CompID) return;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_DELIVERY_CHARGE_RULES, {
        params: {
          CompID,
          PageNumber: 1,
          PageSize: 200,
          SortBy: "Priority",
          SortOrder: "DESC",
        },
      });
      const list = Array.isArray(response.data?.data) ? response.data.data : [];
      setRules(sortRulesForDisplay(list.map(formatRule)));
    } catch (err) {
      showErrorToast("Failed to load delivery charge rules");
    } finally {
      setLoading(false);
    }
  }, [CompID]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const nextPriorityForNewRule = () => {
    const max = rules
      .filter((r) => !r.isDefault)
      .reduce((acc, r) => Math.max(acc, Number(r.priority) || 0), 0);
    return max + 1;
  };

  const openAddModal = () => {
    setIsEditMode(false);
    setForm({ ...emptyRuleForm, priority: nextPriorityForNewRule() });
    setIsModalOpen(true);
  };

  const openEditModal = (rule) => {
    setIsEditMode(true);
    setForm({
      ruleID: rule.ruleID,
      matchType: rule.isDefault
        ? ALL_POSTAL_CODES_VALUE
        : rule.matchType === "Pattern"
          ? "Prefix"
          : rule.matchType || "Exact",
      pattern: rule.isDefault ? "" : rule.pattern || "",
      fee: rule.fee,
      priority: rule.priority,
      isDefault: rule.isDefault,
      isActive: rule.isActive,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (submitting) return;
    setIsModalOpen(false);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleMatchTypeChange = (opt) => {
    const nextValue = opt?.value || "Exact";
    const isAllPostalCodes = nextValue === ALL_POSTAL_CODES_VALUE;
    setForm((prev) => ({
      ...prev,
      matchType: nextValue,
      isDefault: isAllPostalCodes,
      pattern: isAllPostalCodes ? "" : prev.pattern,
      priority: isAllPostalCodes ? 0 : prev.priority || nextPriorityForNewRule(),
    }));
  };

  const validateForm = () => {
    const feeValue = Number(form.fee);
    if (Number.isNaN(feeValue) || feeValue < 0) {
      showErrorToast("Fee must be zero or greater");
      return false;
    }
    const isAllPostalCodes =
      form.isDefault || form.matchType === ALL_POSTAL_CODES_VALUE;
    if (!isAllPostalCodes && !String(form.pattern || "").trim()) {
      showErrorToast("Please enter a postcode pattern for this rule.");
      return false;
    }
    if (
      !isAllPostalCodes &&
      !["Exact", "Prefix"].includes(form.matchType)
    ) {
      showErrorToast("Choose a Match Type");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const isAllPostalCodes =
        form.isDefault || form.matchType === ALL_POSTAL_CODES_VALUE;
      const payload = {
        ruleID: isEditMode ? form.ruleID : 0,
        compID: CompID,
        // Backend accepts Exact/Prefix; default rules use Exact + IsDefault.
        matchType: isAllPostalCodes ? "Exact" : form.matchType,
        pattern: isAllPostalCodes
          ? null
          : form.matchType === "Exact"
            ? normalizeExactPatternList(form.pattern)
            : String(form.pattern || "").trim().toUpperCase(),
        fee: Number(form.fee) || 0,
        priority: isAllPostalCodes
          ? 0
          : isEditMode
            ? Number(form.priority) || 0
            : nextPriorityForNewRule(),
        isDefault: isAllPostalCodes,
        isActive: !!form.isActive,
      };
      const url = isEditMode
        ? APIs.UPDATE_DELIVERY_CHARGE_RULE
        : APIs.ADD_DELIVERY_CHARGE_RULE;
      const response = await axiosInstance.post(url, payload);
      if (response.data?.status) {
        showSuccessToast(
          isEditMode
            ? "Delivery charge rule updated"
            : "Delivery charge rule added"
        );
        setIsModalOpen(false);
        fetchRules();
      } else {
        showErrorToast(response.data?.message || "Failed to save rule");
      }
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        (isEditMode ? "Failed to update rule" : "Failed to add rule");
      showErrorToast(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (rule) => {
    if (rule.isDefault) {
      showErrorToast("The default rule cannot be deleted; edit its fee instead.");
      return;
    }
    const confirmed = window.confirm(
      `Delete rule "${rule.matchType}: ${rule.pattern}"?`
    );
    if (!confirmed) return;
    try {
      const response = await axiosInstance.delete(
        APIs.DELETE_DELIVERY_CHARGE_RULE,
        { params: { RuleID: rule.ruleID, CompID } }
      );
      if (response.data?.status) {
        showSuccessToast("Delivery charge rule deleted");
        fetchRules();
      } else {
        showErrorToast(response.data?.message || "Failed to delete rule");
      }
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Failed to delete rule");
    }
  };

  const persistRulePriorities = async (orderedRules) => {
    const previous = rules;
    setRules(orderedRules);
    setReordering(true);
    try {
      const changed = orderedRules.filter((rule) => {
        const before = previous.find((r) => r.ruleID === rule.ruleID);
        return before && before.priority !== rule.priority;
      });
      for (const rule of changed) {
        const response = await axiosInstance.post(APIs.UPDATE_DELIVERY_CHARGE_RULE, {
          ruleID: rule.ruleID,
          compID: CompID,
          matchType: rule.matchType,
          pattern: rule.isDefault ? null : rule.pattern,
          fee: Number(rule.fee) || 0,
          priority: Number(rule.priority) || 0,
          isDefault: !!rule.isDefault,
          isActive: !!rule.isActive,
        });
        if (!response.data?.status) {
          throw new Error(response.data?.message || "Failed to update priority");
        }
      }
      if (changed.length > 0) {
        showSuccessToast("Rule order updated");
      }
    } catch (err) {
      showErrorToast(
        err?.response?.data?.message || err?.message || "Failed to update rule order"
      );
      setRules(previous);
    } finally {
      setReordering(false);
    }
  };

  const handleDragStart = (e, index) => {
    if (rules[index]?.isDefault || reordering) {
      e.preventDefault();
      return;
    }
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(index));
    const row = e.currentTarget.closest("tr");
    if (row) row.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    if (rules[index]?.isDefault) return;
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = isDarkMode ? "#3d4450" : "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const clearDragRowStyles = () => {
    document
      .querySelectorAll("[data-delivery-rule-row='true']")
      .forEach((tr) => {
        tr.style.backgroundColor = "";
        tr.style.opacity = "1";
      });
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    let draggedIndex;
    try {
      draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
    } catch {
      draggedIndex = dragItem.current;
    }
    const overIndex = dragOverItem.current;
    clearDragRowStyles();

    if (
      draggedIndex === overIndex ||
      draggedIndex == null ||
      overIndex == null ||
      rules[draggedIndex]?.isDefault ||
      rules[overIndex]?.isDefault
    ) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    const next = [...rules];
    const [moved] = next.splice(draggedIndex, 1);
    next.splice(overIndex, 0, moved);
    const reordered = assignPrioritiesFromOrder(next);

    dragItem.current = null;
    dragOverItem.current = null;
    await persistRulePriorities(reordered);
  };

  const handleDragEnd = (e) => {
    const row = e.currentTarget.closest("tr");
    if (row) row.style.opacity = "1";
    clearDragRowStyles();
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const isAllPostalCodesSelected =
    form.isDefault || form.matchType === ALL_POSTAL_CODES_VALUE;

  const selectedMatchType = MATCH_TYPE_OPTIONS.find(
    (opt) =>
      opt.value ===
      (isAllPostalCodesSelected ? ALL_POSTAL_CODES_VALUE : form.matchType)
  );

  const matchTypeInfo = selectedMatchType?.helper ?? "";

  const patternInfo =
    form.matchType === "Exact"
      ? "Separate multiple postcodes with commas. Spacing and capitals are tidied up when you save."
      : "Spacing and capitals are tidied up when you save.";

  return (
    <div className="list-group-item">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center">
          <i className="fas fa-truck me-2" style={{ color: themeColor }}></i>
          <span className="fw-semibold">Delivery Charges by Postcode</span>
        </div>
        <button
          type="button"
          className="btn btn-sm"
          style={{
            backgroundColor: themeColor,
            color: "#fff",
            borderRadius: "8px",
          }}
          onClick={openAddModal}
        >
          <i className="fa fa-plus me-1"></i> Add Rule
        </button>
      </div>

      <p className="text-muted small mb-3">
        We pick the most specific rule that fits the customer’s postcode (longest
        pattern, e.g. LG-3 before LG-). If two rules tie on length, the higher
        priority wins — drag rows to set priority (top = highest). “All postal
        codes” stays at the bottom as the fallback.
      </p>

      <GridSectionLoader loading={loading || reordering} minHeight="120px">
        {rules.length === 0 ? (
          <div className="text-muted small">No delivery charge rules yet.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead
                className="bg-light"
                style={{
                  backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                  color: isDarkMode ? "#ffffff" : "#000000",
                }}
              >
                <tr>
                  <th style={{ width: "40px" }} aria-label="Reorder" />
                  <th>Match Type</th>
                  <th>Pattern</th>
                  <th>Fee</th>
                  <th className="text-center">Active</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule, index) => (
                  <tr
                    key={rule.ruleID}
                    data-delivery-rule-row="true"
                    onDragEnter={(e) => handleDragEnter(e, index)}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <td className="align-middle text-center">
                      {rule.isDefault ? (
                        <span
                          className="text-muted"
                          title="Fallback rule stays at the bottom"
                        >
                          —
                        </span>
                      ) : (
                        <i
                          className="fa fa-bars"
                          draggable={!reordering}
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragEnd={handleDragEnd}
                          style={{
                            cursor: reordering ? "default" : "grab",
                            color: isDarkMode ? "#e2e8f0" : "#6c757d",
                            padding: "6px 8px",
                          }}
                          title="Drag to set priority"
                          aria-label="Drag to set priority"
                          role="button"
                          tabIndex={0}
                        />
                      )}
                    </td>
                    <td>
                      <span className="badge bg-info text-dark">
                        {rule.isDefault ? "All postal codes" : rule.matchType}
                      </span>
                    </td>
                    <td>
                      {rule.isDefault ? (
                        <span className="text-muted fst-italic">—</span>
                      ) : (
                        <code>{rule.pattern}</code>
                      )}
                    </td>
                    <td>
                      {currencySymbol}
                      {Number(rule.fee).toFixed(2)}
                    </td>
                    <td className="text-center">
                      {rule.isActive ? (
                        <span className="badge bg-success">Active</span>
                      ) : (
                        <span className="badge bg-secondary">Inactive</span>
                      )}
                    </td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary me-2"
                        onClick={() => openEditModal(rule)}
                      >
                        <i className="fa fa-edit"></i>
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        disabled={rule.isDefault}
                        onClick={() => handleDelete(rule)}
                        title={
                          rule.isDefault
                            ? "Default rule cannot be deleted"
                            : "Delete rule"
                        }
                      >
                        <i className="fa fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GridSectionLoader>

      <CustomModal
        title={isEditMode ? "Edit Delivery Charge Rule" : "Add Delivery Charge Rule"}
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleSubmit}
        showButton
        submitButtonText={isEditMode ? "Update" : "Add"}
        submitButtonDisabled={submitting}
        isCentered
        modalSize="modal-lg"
        minHeight="380px"
        maxHeight="640px"
      >
        <div className="row g-3">
          <div className={isAllPostalCodesSelected ? "col-md-12" : "col-md-6"}>
            <FieldLabelWithInfo labelText="Match Type" infoText={matchTypeInfo} />
            <Select
              value={selectedMatchType}
              options={MATCH_TYPE_OPTIONS}
              styles={dropdownStyles}
              onChange={handleMatchTypeChange}
            />
          </div>
          {!isAllPostalCodesSelected ? (
            <div className="col-md-6">
              <FieldLabelWithInfo labelText="Pattern" infoText={patternInfo} />
              <FormInput
                type="text"
                value={form.pattern || ""}
                onchange={(e) => handleFormChange("pattern", e.target.value)}
                placeHolder={
                  form.matchType === "Exact"
                    ? "e.g. SW1A 1AA, EH1 1YZ"
                    : "e.g. SW1"
                }
                name="pattern"
              />
            </div>
          ) : null}
          <div className="col-md-6">
            <label className="form-label fw-semibold">Fee</label>
            <FormInput
              type="number"
              value={form.fee}
              onchange={(e) => handleFormChange("fee", e.target.value)}
              placeHolder={`${currencySymbol}0.00`}
              name="fee"
              min="0"
              step="0.01"
              prepend={currencySymbol}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label fw-semibold d-block">&nbsp;</label>
            <div className="form-check form-switch mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="rule-active"
                checked={!!form.isActive}
                onChange={(e) =>
                  handleFormChange("isActive", e.target.checked)
                }
              />
              <label className="form-check-label ms-1" htmlFor="rule-active">
                Active
              </label>
            </div>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default DeliveryChargeRules;
